// Catalog Shell - Toast Notification Manager
// ============================================================================

import { createElement } from '../utils/dom';
import { icons } from '../utils/icons';
import type { IDisposable } from '../components/types';

// ============================================================================
// Types
// ============================================================================

export type ToastVariant = 'info' | 'success' | 'warning' | 'error';

export interface ToastOptions {
    /** The message to display */
    message: string;
    /** Visual variant. Default: 'info' */
    variant?: ToastVariant;
    /** Auto-dismiss duration in ms. Default: 4000. Set to 0 for no auto-dismiss. */
    duration?: number;
    /** Optional action button */
    action?: {
        label: string;
        onClick: () => void;
    };
}

export interface ToastHandle {
    /** Dismiss this toast immediately */
    dismiss(): void;
}

// ============================================================================
// Internal
// ============================================================================

interface ToastEntry {
    element: HTMLElement;
    timeout?: number;
    dismissed: boolean;
}

const MAX_VISIBLE = 5;
const DEFAULT_DURATION = 4000;

const VARIANT_ICONS: Record<ToastVariant, () => SVGSVGElement> = {
    info: icons.info,
    success: icons.success,
    warning: icons.warning,
    error: icons.error,
};

// ============================================================================
// ToastManager
// ============================================================================

export class ToastManager implements IDisposable {
    private container: HTMLElement;
    private toasts: ToastEntry[] = [];
    private disposed = false;

    constructor(parentContainer: HTMLElement) {
        this.container = createElement('div', {
            className: 'cs-toast-container',
        });
        parentContainer.appendChild(this.container);
    }

    /** Show a toast notification */
    show(options: ToastOptions): ToastHandle {
        if (this.disposed) return { dismiss: () => {} };

        const variant = options.variant ?? 'info';
        const duration = options.duration ?? DEFAULT_DURATION;

        // Evict oldest if at capacity
        while (this.toasts.length >= MAX_VISIBLE) {
            this.dismissEntry(this.toasts[0]);
        }

        // Build toast DOM
        const toast = createElement('div', {
            className: `cs-toast cs-toast--${variant}`,
        });

        // Icon
        const iconEl = createElement('div', { className: 'cs-toast-icon' });
        iconEl.appendChild(VARIANT_ICONS[variant]());
        toast.appendChild(iconEl);

        // Message
        const messageEl = createElement('div', {
            className: 'cs-toast-message',
            textContent: options.message,
        });
        toast.appendChild(messageEl);

        // Optional action button
        if (options.action) {
            const actionBtn = createElement('button', {
                className: 'cs-toast-action',
                textContent: options.action.label,
            });
            const actionHandler = options.action.onClick;
            actionBtn.addEventListener('click', () => {
                actionHandler();
                this.dismissEntry(entry);
            });
            toast.appendChild(actionBtn);
        }

        // Dismiss button
        const dismissBtn = createElement('button', {
            className: 'cs-toast-dismiss',
        });
        dismissBtn.appendChild(icons.close());
        dismissBtn.addEventListener('click', () => this.dismissEntry(entry));
        toast.appendChild(dismissBtn);

        // Create entry
        const entry: ToastEntry = {
            element: toast,
            dismissed: false,
        };

        // Auto-dismiss
        if (duration > 0) {
            entry.timeout = window.setTimeout(() => this.dismissEntry(entry), duration);
        }

        this.toasts.push(entry);
        this.container.appendChild(toast);

        return {
            dismiss: () => this.dismissEntry(entry),
        };
    }

    /** Convenience: show a success toast */
    success(message: string, duration?: number): ToastHandle {
        return this.show({ message, variant: 'success', duration });
    }

    /** Convenience: show an error toast */
    error(message: string, duration?: number): ToastHandle {
        return this.show({ message, variant: 'error', duration: duration ?? 6000 });
    }

    /** Convenience: show a warning toast */
    warning(message: string, duration?: number): ToastHandle {
        return this.show({ message, variant: 'warning', duration });
    }

    /** Convenience: show an info toast */
    info(message: string, duration?: number): ToastHandle {
        return this.show({ message, variant: 'info', duration });
    }

    /** Dismiss all visible toasts */
    dismissAll(): void {
        for (const entry of [...this.toasts]) {
            this.dismissEntry(entry);
        }
    }

    dispose(): void {
        if (this.disposed) return;
        this.disposed = true;
        this.dismissAll();
        this.container.remove();
    }

    // ========================================================================
    // Internal
    // ========================================================================

    private dismissEntry(entry: ToastEntry): void {
        if (entry.dismissed) return;
        entry.dismissed = true;

        if (entry.timeout !== undefined) {
            clearTimeout(entry.timeout);
        }

        // Animate out
        entry.element.classList.add('cs-toast--dismissing');
        entry.element.addEventListener('animationend', () => {
            entry.element.remove();
        }, { once: true });

        // Fallback removal in case animationend doesn't fire
        setTimeout(() => entry.element.remove(), 200);

        const idx = this.toasts.indexOf(entry);
        if (idx !== -1) {
            this.toasts.splice(idx, 1);
        }
    }
}
